import styled, { css, keyframes } from 'styled-components'
import { Colors, Spacing } from 'cio-design-library/design-tokens'
import Shadows from 'builder/styles/shadows'
import { Typography, FontWeights } from 'builder/styles/typography'
import Media from 'builder/styles/media'

const scrollbar = css`
  ::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
`

const dotFlashingAnimation = keyframes`
  0% {
    background-color: ${Colors.Indigo50};
  }
  25%, 75% {
    background-color: ${Colors.Indigo10};
  }
  100% {
    background-color: ${Colors.Indigo50};
  }
`

export const LoadingDots = styled.div`
  position: relative;
  width: 10px;
  height: 10px;
  border-radius: 5px;
  background-color: ${Colors.Indigo50};
  animation: ${dotFlashingAnimation} 1s infinite linear alternate;

  &::before,
  &::after {
    content: '';
    display: inline-block;
    position: absolute;
    top: 0;
    width: 10px;
    height: 10px;
    border-radius: 5px;
    background-color: ${Colors.Indigo50};
    animation: ${dotFlashingAnimation} 1s infinite alternate;
  }

  &::before {
    left: -15px;
    animation-delay: -0.33s;
  }

  &::after {
    left: 15px;
    animation-delay: 0.33s;
  }
`

export const ChatBotContainer = styled.div`
  flex: 1;
  max-width: 100%;
  height: 100%;
  background-color: ${Colors.White};
  margin: auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`

export const Close = styled.div`
  position: absolute;
  top: 18px;
  right: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${Colors.Neutral70};
  cursor: pointer;
  transition: color 0.1s;
  background-color: ${Colors.White};
  border-radius: 100%;
  padding: 4px;

  &:hover {
    color: ${Colors.Blue50};
    background-color: ${Colors.Blue10};
  }
`

export const WelcomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  align-items: center;
  text-align: center;
`

export const WelcomeContent = styled.div`
  width: 100%;
`

export const WelcomeTitle = styled.h1`
  ${Typography.Subhead};
  ${FontWeights.Medium};
  color: ${Colors.Neutral90};
  margin-top: 12px;
`

export const WelcomeSubtitle = styled.p`
  ${Typography.Caption};
  color: ${Colors.Neutral50};
  text-align: center;
  max-width: 368px;
  margin: 8px auto 32px;

  ${Media.Phone`
    max-width: unset;
    padding: 0 24px;  
  `}
`

export const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  padding-top: 24px;
  justify-content: center;
  gap: 4px;
`

export const ChatTitle = styled.h2`
  ${Typography.Body};
  color: ${Colors.Neutral90};
  ${FontWeights.Medium};
`

export const DateSeparator = styled.div`
  ${Typography.Caption};
  color: ${Colors.Neutral70};
  background-color: ${Colors.Neutral5};
  border: 0.5px solid ${Colors.Neutral15};
  padding: 4px 8px;
  width: fit-content;
  margin: 0 auto 38px;
  border-radius: 40px;
`

export const MessageContainer = styled.div<{ isUser: boolean }>`
  display: flex;
  align-items: ${props => (props.isUser ? 'flex-end' : 'flex-start')};
  flex-direction: column;
  gap: 8px;
`

export const Reactions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
  margin-top: 2px;
`

export const ApplySuggestionsContainer = styled.div<{
  $state?: 'idle' | 'applying' | 'applied'
  $disabled?: boolean
}>`
  display: flex;
  gap: 2px;
  align-items: center;
  border-radius: 8px;
  cursor: ${props => (props.$disabled ? 'default' : 'pointer')};
  margin-left: auto;
  padding: 4px 4px 4px 8px;
  transition: all 0.2s ease;

  background-color: ${props => {
    if (props.$state === 'applying') return Colors.Neutral10
    if (props.$state === 'applied') return Colors.Green10
    return Colors.Indigo10
  }};

  &:hover {
    ${props =>
      !props.$disabled &&
      props.$state === 'idle' &&
      css`
        background-color: ${Colors.Indigo50};

        img {
          filter: brightness(0) invert(1);
        }
      `}
  }

  &:active {
    ${props =>
      !props.$disabled &&
      props.$state === 'idle' &&
      css`
        background-color: ${Colors.Indigo60};
        transform: scale(0.98);

        img {
          filter: brightness(0) invert(1);
        }
      `}
  }

  svg {
    transition: color 0.2s ease;
    ${props => {
      if (props.$state === 'applying')
        return css`
          color: ${Colors.Neutral60};
        `
      if (props.$state === 'applied')
        return css`
          color: ${Colors.Green50};
        `
      return css`
        color: ${Colors.Indigo80};
      `
    }}
  }
`

export const ApplySuggestions = styled.span<{ $state?: 'idle' | 'applying' | 'applied' }>`
  color: ${props => {
    if (props.$state === 'applying') return Colors.Neutral50
    if (props.$state === 'applied') return Colors.Green80
    return Colors.Indigo80
  }};
  ${FontWeights.Medium};
  ${Typography.Caption};

  ${ApplySuggestionsContainer}:hover & {
    ${props =>
      props.$state === 'idle' &&
      css`
        color: white;
      `}
    ${props =>
      props.$state === 'applied' &&
      css`
        color: ${Colors.Green80};
      `}
  }
`

export const MessageBubble = styled.div<{ isUser: boolean }>`
  width: fit-content;
  padding: 12px 16px;
  ${Typography.Body};
  color: ${Colors.Neutral90};
  max-width: 100%;

  ${props =>
    props.isUser
      ? css`
          background-color: ${Colors.Neutral10};
          border-radius: 16px ${Spacing.S} 0 16px;
        `
      : css`
          background-color: ${Colors.Indigo10};
          border-radius: 16px ${Spacing.S} ${Spacing.S} 0;
        `}
`

export const MessagesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 72px 24px;
  gap: 8px;

  ${Media.Phone`
    padding: 0 16px 24px;
  `}
`

export const PromptInput = styled.textarea.attrs<{ ref?: React.Ref<HTMLTextAreaElement> }>(
  props => ({
    ref: props.ref,
  }),
)`
  position: relative;
  border: 1px solid ${Colors.Neutral30};
  width: 100%;
  min-height: 64px;
  max-height: 200px;
  border-radius: 16px;
  padding: 16px 52px 16px 20px;
  font-size: 16px;
  resize: none;
  overflow-y: auto;
  outline: none;
  text-align: start;
  vertical-align: middle;
  box-shadow: 0 0 1px 0 rgba(15, 56, 113, 0.32), 0 2px 4px -1px rgba(15, 56, 113, 0.08),
    0 8px 20px -4px rgba(15, 56, 113, 0.12);

  ${Typography.Body};
  ${scrollbar};

  &:focus {
    border-color: ${Colors.Blue50};
    outline: none;
  }

  &::placeholder {
    color: ${Colors.Neutral50};
  }
`

export const ChatBotForm = styled.form<{ $withPadding?: boolean }>`
  position: relative;
  height: 100%;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  overflow: hidden;

  ${props =>
    props.$withPadding &&
    css`
      ${Media.Phone`
            padding: 0 24px;  
          `}
    `}
`

export const ChatBotBody = styled.div`
  position: relative;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  margin-top: auto;
  ${scrollbar}
`

export const Loader = styled.div`
  display: flex;
  gap: 26px;
  align-items: center;
`

const SpinKeyframe = keyframes`
  from {transform: rotate(0deg);}
  to {transform: rotate(360deg);}
`

export const SpinAnimation = styled.span`
  svg {
    width: 100%;
    height: 100%;
    animation: ${SpinKeyframe} 2s linear infinite;
  }
  img {
    animation: ${SpinKeyframe} 2s linear infinite;
  }
`

export const Avatar = styled.span<{ visibility?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  background-color: ${Colors.Indigo10};
  flex-shrink: 0;
  padding: 5px;
  visibility: ${({ visibility }) => visibility || 'visible'};
`

export const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${Colors.Red50};
  background: ${Colors.Red10};
  border: 1px solid ${Colors.Red10};
  border-radius: 16px ${Spacing.S} ${Spacing.S} 0;
  padding: 16px;
  ${Typography.Caption};
  width: fit-content;
`

export const IconWrapper = styled.span`
  display: inline-block;
  border: none;
  cursor: pointer;

  svg path {
    fill: currentColor;
    stroke: none;
    transition: fill 0.3s ease;
  }

  &:hover svg path {
    fill: ${Colors.Blue50};
    stroke: none;
  }
`

export const ScrollIndicator = styled.button`
  position: sticky;
  background: white;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: 1px solid ${Colors.Neutral10};
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 999;
  ${Shadows.lightWithBorder.low};
`

export const Copy = styled.span`
  color: ${Colors.Neutral60};
  ${FontWeights.Medium};
  ${Typography.Caption};
  margin-left: 13px;
  display: flex;
  align-items: center;
  margin-bottom: auto;
  gap: 2px;
  cursor: pointer;
  border-radius: 8px;
  background-color: transparent;
  transition: all 0.2s ease;
  padding: 4px 4px 4px 8px;

  &:hover {
    color: ${Colors.Indigo60};
    background-color: ${Colors.Indigo20};
  }

  &:active {
    color: ${Colors.Indigo70};
    background-color: ${Colors.Indigo30};
    transform: scale(0.98);
  }

  svg {
    transition: color 0.2s ease;
  }
`

export const ReactionButton = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background-color: ${props => (props.$selected ? Colors.Blue10 : 'transparent')};
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 4px;
  color: ${props => (props.$selected ? Colors.Blue50 : Colors.Neutral60)};

  &:hover {
    color: ${Colors.Blue50};
    background-color: ${Colors.Blue10};
  }

  &:active {
    color: ${Colors.Blue60};
    background-color: ${Colors.Blue20};
    transform: scale(0.95);
  }
`

export const Toast = styled.div`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: ${Colors.Neutral80};
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`

export const InputContainer = styled.div`
  width: 100%;
  padding: 0 24px 24px 24px;
  position: relative;

  ${Media.Phone`
    padding: 0 12px 16px;
  `}
`

export const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`

export const SendButton = styled.button`
  position: absolute;
  right: 12px;
  top: 18px;
  width: 24px;
  height: 24px;
  border: none;
  background: ${Colors.Blue50};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
  z-index: 1;

  &:hover {
    background: ${Colors.Blue60};
  }

  &:active {
    background: ${Colors.Blue70};
  }
`

// Markdown styles
export const Link = styled.a`
  color: ${Colors.Indigo50};
  ${FontWeights.Medium};

  &:hover {
    color: ${Colors.Indigo70};
  }
`

export const Strong = styled.strong`
  ${FontWeights.Medium};
`

export const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 8px 0 8px 36px;
  padding: 0;
`

export const Heading = styled.h3`
  margin-top: 20px;
  margin-bottom: 8px;
  ${FontWeights.Medium};
`

export const SuggestionsContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 24px;

  ${Media.Phone`
    flex-wrap: wrap;  
  `}
`

export const SuggestionButton = styled.div`
  ${Typography.Caption};
  color: ${Colors.Neutral90};
  background: ${Colors.Neutral10};
  border: 1px solid ${Colors.Neutral15};
  border-radius: ${Spacing['3XS']};
  padding: ${Spacing['3XS']} ${Spacing.XS};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${Colors.Neutral20};
    border-color: ${Colors.Neutral30};
  }

  &:active {
    transform: scale(0.98);
    background: ${Colors.Neutral30};
  }
`
